import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Globe, MapPin, Wrench, LogOut, Loader2, 
  RefreshCw, Edit2, Save, X, CreditCard, CheckCircle, AlertCircle,
  MessageSquare, Eye, Calendar, DollarSign, Upload, Image as ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  tradieGetProfile, tradieUpdateProfile, tradieGetEnquiries, 
  tradieMarkEnquiryRead, tradieGetSubscription, tradieCancelSubscription,
  createCheckoutSession, getRegions, getCategories, uploadLogo, resolveLogoUrl
} from '@/lib/api';

const TradiePortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  const getToken = () => localStorage.getItem('tradieToken');

  const loadData = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/tradie/login');
      return;
    }

    setLoading(true);
    try {
      const [profileData, enquiriesData, subscriptionData, regionsData, categoriesData] = await Promise.all([
        tradieGetProfile(token),
        tradieGetEnquiries(token),
        tradieGetSubscription(token),
        getRegions(),
        getCategories()
      ]);
      setProfile(profileData);
      setEnquiries(enquiriesData);
      setSubscription(subscriptionData);
      setRegions(regionsData);
      setCategories(categoriesData);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('tradieToken');
        navigate('/tradie/login');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = () => {
    localStorage.removeItem('tradieToken');
    localStorage.removeItem('tradieEmail');
    localStorage.removeItem('tradieBusinessName');
    navigate('/tradie/login');
    toast.success('Logged out successfully');
  };

  const startEditing = () => {
    setEditData({
      business_name: profile.business_name,
      contact_name: profile.contact_name,
      phone: profile.phone,
      website: profile.website || '',
      description: profile.description,
      regions: [...profile.regions],
      categories: [...profile.categories],
      logo_url: profile.logo_url || ''
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditData({});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await tradieUpdateProfile(getToken(), editData);
      await loadData();
      setEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRegionToggle = (region) => {
    setEditData(prev => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter(r => r !== region)
        : [...prev.regions, region]
    }));
  };

  const handleCategoryToggle = (category) => {
    setEditData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please use a PNG, JPG, WEBP or GIF image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be smaller than 5MB');
      return;
    }
    setLogoUploading(true);
    try {
      const result = await uploadLogo(file);
      setEditData(prev => ({ ...prev, logo_url: result.logo_url }));
      toast.success('Logo uploaded. Click Save to apply.');
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoRemove = () => {
    setEditData(prev => ({ ...prev, logo_url: '' }));
  };

  const handleMarkRead = async (enquiryId) => {
    try {
      await tradieMarkEnquiryRead(getToken(), enquiryId);
      setEnquiries(prev => prev.map(e => 
        e.id === enquiryId ? { ...e, status: 'read' } : e
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const result = await tradieCancelSubscription(getToken());
      toast.success('Subscription cancelled', {
        description: `Your listing will remain active until ${new Date(result.ends_at).toLocaleDateString()}`
      });
      setCancelDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const handleSubscribe = async () => {
    try {
      const result = await createCheckoutSession(profile.id);
      window.location.href = result.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const newEnquiriesCount = enquiries.filter(e => e.status === 'new').length;

  return (
    <div className="min-h-screen bg-background" data-testid="tradie-portal">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <User className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <span className="font-heading font-bold text-lg">{profile?.business_name}</span>
                <span className="block text-xs text-muted-foreground -mt-0.5">Tradie Portal</span>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Banner */}
        {profile?.status !== 'approved' && (
          <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Your account is pending approval</p>
              <p className="text-sm text-amber-700">Our team will review your application shortly.</p>
            </div>
          </div>
        )}

        {profile?.status === 'approved' && subscription?.status !== 'active' && (
          <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Complete your subscription</p>
                <p className="text-sm text-muted-foreground">Subscribe for $49 NZD/month to make your listing visible.</p>
              </div>
            </div>
            <Button onClick={handleSubscribe} className="bg-primary hover:bg-primary/90" data-testid="subscribe-btn">
              Subscribe Now
            </Button>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">
              <MessageSquare className="w-4 h-4 mr-2" />
              Enquiries
              {newEnquiriesCount > 0 && (
                <Badge className="ml-2 bg-primary text-primary-foreground">{newEnquiriesCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="subscription" data-testid="tab-subscription">
              <CreditCard className="w-4 h-4 mr-2" />
              Subscription
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Business Profile</CardTitle>
                  <CardDescription>Manage your listing information</CardDescription>
                </div>
                {!editing ? (
                  <Button variant="outline" onClick={startEditing} data-testid="edit-profile-btn">
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={cancelEditing}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-profile-btn">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                {editing ? (
                  <>
                    {/* Logo Upload */}
                    <div className="space-y-2">
                      <Label>Business Logo</Label>
                      <div className="flex items-center gap-4">
                        {editData.logo_url ? (
                          <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-stone-200 bg-white flex-shrink-0">
                            <img
                              src={resolveLogoUrl(editData.logo_url)}
                              alt="Logo"
                              className="w-full h-full object-contain"
                              data-testid="portal-logo-preview"
                            />
                            <button
                              type="button"
                              onClick={handleLogoRemove}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center hover:bg-destructive/90"
                              aria-label="Remove logo"
                              data-testid="portal-logo-remove"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-24 h-24 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="w-6 h-6 text-stone-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            onChange={handleLogoUpload}
                            className="hidden"
                            data-testid="portal-logo-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={logoUploading}
                            data-testid="portal-logo-choose"
                          >
                            {logoUploading ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                            ) : (
                              <><Upload className="w-4 h-4 mr-2" />{editData.logo_url ? 'Change Logo' : 'Upload Logo'}</>
                            )}
                          </Button>
                          <p className="text-xs text-muted-foreground mt-2">PNG, JPG, WEBP or GIF. Max 5MB.</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Business Name</Label>
                        <Input
                          value={editData.business_name}
                          onChange={(e) => setEditData({ ...editData, business_name: e.target.value })}
                          data-testid="edit-business-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Name</Label>
                        <Input
                          value={editData.contact_name}
                          onChange={(e) => setEditData({ ...editData, contact_name: e.target.value })}
                          data-testid="edit-contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={editData.phone}
                          onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                          data-testid="edit-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          value={editData.website}
                          onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                          placeholder="www.example.com"
                          data-testid="edit-website"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        rows={4}
                        data-testid="edit-description"
                      />
                    </div>
                    
                    <div className="space-y-3">
                      <Label>Service Categories</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {categories.map((category) => (
                          <label 
                            key={category}
                            className={`flex items-center space-x-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                              editData.categories?.includes(category) 
                                ? 'border-primary bg-primary/5' 
                                : 'border-stone-200 hover:border-stone-300'
                            }`}
                          >
                            <Checkbox
                              checked={editData.categories?.includes(category)}
                              onCheckedChange={() => handleCategoryToggle(category)}
                            />
                            <span className="text-sm">{category}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <Label>Service Regions</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {regions.map((region) => (
                          <label 
                            key={region}
                            className={`flex items-center space-x-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                              editData.regions?.includes(region) 
                                ? 'border-primary bg-primary/5' 
                                : 'border-stone-200 hover:border-stone-300'
                            }`}
                          >
                            <Checkbox
                              checked={editData.regions?.includes(region)}
                              onCheckedChange={() => handleRegionToggle(region)}
                            />
                            <span className="text-sm">{region}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Logo display */}
                    {profile?.logo_url && (
                      <div className="flex items-start gap-4 pb-4 border-b border-stone-100">
                        <img
                          src={resolveLogoUrl(profile.logo_url)}
                          alt={`${profile.business_name} logo`}
                          className="w-24 h-24 rounded-lg object-contain bg-white border border-stone-200"
                          data-testid="portal-profile-logo"
                        />
                        <div>
                          <p className="text-sm text-muted-foreground">Business Logo</p>
                          <p className="text-xs text-muted-foreground mt-1">Visible on your public listing</p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-start gap-3">
                        <User className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Business Name</p>
                          <p className="font-medium">{profile?.business_name}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <User className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Contact Name</p>
                          <p className="font-medium">{profile?.contact_name}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <p className="font-medium">{profile?.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <p className="font-medium">{profile?.phone}</p>
                        </div>
                      </div>
                      {profile?.website && (
                        <div className="flex items-start gap-3">
                          <Globe className="w-5 h-5 text-primary mt-0.5" />
                          <div>
                            <p className="text-sm text-muted-foreground">Website</p>
                            <a href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                              {profile.website}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Description</p>
                      <p className="text-foreground">{profile?.description}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                        <Wrench className="w-4 h-4" />
                        Services
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {profile?.categories.map(cat => (
                          <Badge key={cat} variant="secondary">{cat}</Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Regions
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {profile?.regions.map(reg => (
                          <Badge key={reg} variant="outline">{reg}</Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Enquiries</CardTitle>
                <CardDescription>Messages from potential customers</CardDescription>
              </CardHeader>
              <CardContent>
                {enquiries.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No enquiries yet</p>
                    <p className="text-sm text-muted-foreground">Enquiries will appear here when customers contact you.</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-3">
                      {enquiries.map((enquiry) => (
                        <div 
                          key={enquiry.id}
                          className={`p-4 rounded-lg border cursor-pointer hover:bg-stone-50 transition-colors ${
                            enquiry.status === 'new' ? 'border-primary bg-primary/5' : 'border-stone-200'
                          }`}
                          onClick={() => {
                            setSelectedEnquiry(enquiry);
                            if (enquiry.status === 'new') {
                              handleMarkRead(enquiry.id);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium">{enquiry.name}</p>
                              <p className="text-sm text-muted-foreground">{enquiry.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {enquiry.status === 'new' && (
                                <Badge className="bg-primary text-primary-foreground">New</Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {new Date(enquiry.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm line-clamp-2">{enquiry.message}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Subscription</CardTitle>
                <CardDescription>Manage your monthly subscription</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Status */}
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                      subscription?.status === 'active' || subscription?.status === 'trial' || subscription?.is_free ? 'bg-secondary/20' : 'bg-stone-100'
                    }`}>
                      {(subscription?.status === 'active' || subscription?.status === 'trial' || subscription?.is_free) ? (
                        <CheckCircle className="w-8 h-8 text-secondary" />
                      ) : (
                        <CreditCard className="w-8 h-8 text-stone-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-heading font-semibold text-xl">
                        {subscription?.is_free ? 'Free Subscription' :
                         subscription?.status === 'trial' ? 'Free Trial' :
                         subscription?.status === 'active' ? 'Active Subscription' : 
                         subscription?.status === 'cancelled' ? 'Cancelled' : 'No Active Subscription'}
                      </p>
                      <p className="text-muted-foreground">
                        {subscription?.is_free ? 'Your listing is visible to homeowners (free)' :
                         subscription?.status === 'trial' ? `Trial ends in ${subscription?.trial_days_remaining} days` :
                         subscription?.status === 'active' ? 'Your listing is visible to homeowners' :
                         subscription?.status === 'cancelled' ? `Active until ${new Date(subscription.ends_at).toLocaleDateString()}` :
                         'Subscribe to make your listing visible'}
                      </p>
                    </div>
                  </div>

                  {/* Subscription Details */}
                  {(subscription?.status === 'active' || subscription?.status === 'trial') && !subscription?.is_free && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 rounded-lg bg-stone-50">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <DollarSign className="w-4 h-4" />
                          <span className="text-sm">Monthly Fee</span>
                        </div>
                        <p className="font-heading font-semibold text-xl">$49 NZD</p>
                      </div>
                      {subscription?.status === 'trial' ? (
                        <>
                          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                            <div className="flex items-center gap-2 text-blue-700 mb-1">
                              <Calendar className="w-4 h-4" />
                              <span className="text-sm">Trial Ends</span>
                            </div>
                            <p className="font-heading font-semibold text-blue-900">
                              {subscription.trial_ends_at ? new Date(subscription.trial_ends_at).toLocaleDateString() : '—'}
                            </p>
                          </div>
                          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                            <div className="flex items-center gap-2 text-blue-700 mb-1">
                              <Calendar className="w-4 h-4" />
                              <span className="text-sm">Days Remaining</span>
                            </div>
                            <p className="font-heading font-semibold text-blue-900">
                              {subscription.trial_days_remaining} days
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="p-4 rounded-lg bg-stone-50">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                              <Calendar className="w-4 h-4" />
                              <span className="text-sm">Started</span>
                            </div>
                            <p className="font-heading font-semibold">
                              {subscription.started_at ? new Date(subscription.started_at).toLocaleDateString() : '—'}
                            </p>
                          </div>
                          <div className="p-4 rounded-lg bg-stone-50">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1">
                              <Calendar className="w-4 h-4" />
                              <span className="text-sm">Next Billing</span>
                            </div>
                            <p className="font-heading font-semibold">
                              {subscription.ends_at ? new Date(subscription.ends_at).toLocaleDateString() : '—'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Free subscription notice */}
                  {subscription?.is_free && (
                    <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                      <p className="font-medium text-purple-900">Your listing is free</p>
                      <p className="text-sm text-purple-700">No payment required. Your listing is visible to all homeowners.</p>
                    </div>
                  )}

                  {/* Trial notice with upgrade option */}
                  {subscription?.status === 'trial' && !subscription?.is_free && (
                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                      <p className="font-medium text-amber-900">Your free trial will end soon</p>
                      <p className="text-sm text-amber-700 mb-3">Subscribe now to keep your listing visible after the trial ends.</p>
                      <Button onClick={handleSubscribe} size="sm" className="bg-primary hover:bg-primary/90">
                        Subscribe for $49/month
                      </Button>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="pt-4 border-t">
                    {subscription?.is_free ? (
                      <p className="text-muted-foreground">Your subscription is managed by the administrator.</p>
                    ) : subscription?.status === 'active' ? (
                      <Button 
                        variant="outline" 
                        className="text-destructive border-destructive hover:bg-destructive/10"
                        onClick={() => setCancelDialogOpen(true)}
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel Subscription
                      </Button>
                    ) : subscription?.status === 'cancelled' ? (
                      <Button onClick={handleSubscribe} className="bg-primary hover:bg-primary/90" data-testid="resubscribe-btn">
                        Resubscribe
                      </Button>
                    ) : subscription?.status === 'trial' ? (
                      null // Subscribe button already shown above
                    ) : subscription?.can_subscribe ? (
                      <Button onClick={handleSubscribe} className="bg-primary hover:bg-primary/90" data-testid="subscribe-btn-tab">
                        Subscribe for $49/month
                      </Button>
                    ) : (
                      <p className="text-muted-foreground">Your account must be approved before you can subscribe.</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Enquiry Detail Dialog */}
      <Dialog open={!!selectedEnquiry} onOpenChange={() => setSelectedEnquiry(null)}>
        <DialogContent>
          {selectedEnquiry && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Enquiry from {selectedEnquiry.name}</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <a href={`mailto:${selectedEnquiry.email}`} className="font-medium text-primary hover:underline">
                      {selectedEnquiry.email}
                    </a>
                  </div>
                  {selectedEnquiry.phone && (
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <a href={`tel:${selectedEnquiry.phone}`} className="font-medium text-primary hover:underline">
                        {selectedEnquiry.phone}
                      </a>
                    </div>
                  )}
                </div>
                
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Message</p>
                  <p className="text-sm bg-stone-50 p-3 rounded-lg whitespace-pre-wrap">{selectedEnquiry.message}</p>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Received: {new Date(selectedEnquiry.created_at).toLocaleString()}
                </p>
                
                <div className="flex gap-2 pt-4 border-t">
                  <Button asChild className="flex-1 bg-primary hover:bg-primary/90">
                    <a href={`mailto:${selectedEnquiry.email}?subject=Re: Your enquiry to ${profile?.business_name}`}>
                      <Mail className="w-4 h-4 mr-2" />
                      Reply via Email
                    </a>
                  </Button>
                  {selectedEnquiry.phone && (
                    <Button asChild variant="outline" className="flex-1">
                      <a href={`tel:${selectedEnquiry.phone}`}>
                        <Phone className="w-4 h-4 mr-2" />
                        Call
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              Your listing will remain visible until the end of your current billing period. 
              After that, it will be hidden from the directory until you resubscribe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelSubscription}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TradiePortal;
